<script setup lang="ts">
import DRDialog from "@/components/UI/feedbacks/DRDialog.vue";
import ProjectAvatar from "@/components/projects/ProjectAvatar.vue";
import DebounceInput from "@/components/UI/form/DebounceInput.vue";

import { computed, ref } from "vue";
import { useGetProjects } from "@/stores/queries";
import type { DRPartialProject, PaginationParams, Project } from "@/types";

const props = defineProps<{
  disabledProject?: number;
  modelValue?: boolean;
  title: string;
  description?: string;
  loading?: boolean;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "select", projectId: number): void;
}>();

const show = computed({
  get: () => props.modelValue,
  set: (value) => {
    emits("update:modelValue", value);
  },
});

const cancel = () => {
  show.value = false;
};

const pagination = ref<PaginationParams>({
  skip: 0,
  take: 8,
  sortBy: "name",
  order: "asc",
});

const query = ref("");
const filters = computed(() => ({
  search: query.value,
}));

const { data, localData } = useGetProjects(pagination, filters, false);

const projects = computed<(Project | DRPartialProject)[]>(() => {
  const res = data.value?.projects ?? localData.value ?? [];
  return res.filter((project) => project.id !== props.disabledProject);
});
</script>

<template>
  <DRDialog
    v-model="show"
    :title="title"
    @cancel="cancel()"
    :loading="loading"
    hide-footer
  >
    <div>
      <div class="mb-3 text-grey" v-if="description">{{ description }}</div>
      <DebounceInput
        v-model="query"
        :placeholder="$t('search')"
        :debounce="500"
        class="mb-2 w-100"
        size="large"
        search
      />
      <ul class="list-group w-100">
        <li
          v-for="project in projects"
          :key="project.id"
          class="list-group-item list-group-item-action cursor-pointer hoverable-card"
          @click="$emit('select', project.id)"
        >
          <div class="d-flex gap-2 align-items-center">
            <ProjectAvatar :project="project" :size="30" />
            <div>{{ project.name }}</div>
          </div>
        </li>
      </ul>
    </div>
  </DRDialog>
</template>

<style lang="scss" scoped></style>
